<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useSessionStore } from '@Stores';
import { useCartSteps } from '../../../composables';
import { CART_STEPS } from '../../../enums';
import { FetchAnimalsMedicalCostsService } from '../../../services';
import useShoppingCartStore from '../../../store';

const { animals, cartData, hasCart, isIntake, isOutcome, loading, products, services } =
  storeToRefs(useShoppingCartStore());
const { fetchActiveDiscounts } = useShoppingCartStore();
const { canEditSummaryItems, shelterProfile } = storeToRefs(useSessionStore());

const { currentStep } = useCartSteps();

const isEditingAnimals = ref(false);
const isEditingProducts = ref(false);
const productsInCollapsed = ref(false);

const showMedicalCosts = computed(() => shelterProfile.value?.track_medical_costs && isOutcome.value);
const showRetailSummary = computed(() => !isIntake.value && !!shelterProfile.value?.enable_products);

onMounted(async () => await onInitStep());

async function onInitStep() {
  currentStep.value = CART_STEPS.SUMMARY;

  loading.value = true;

  await Promise.all([fetchMedicalCosts(), fetchActiveDiscounts()]).finally(() => (loading.value = false));
}

async function fetchMedicalCosts() {
  if (!isOutcome.value) return;

  await FetchAnimalsMedicalCostsService().then(({ data }) => (cartData.value = data));
}
</script>

<template>
  <div class="flex flex-col gap-0">
    <MPanel
      v-if="animals.length > 0"
      class="h-full border-none"
      pt:header:class="px-0"
      pt:toggleableContent="h-full flex flex-col"
      pt:content:class="flex-1 shrink-0 basis-auto px-0"
      pt:footer:class="shrink-0"
    >
      <template #customHeader>
        <div class="flex w-full flex-wrap items-center justify-between gap-2 pr-4">
          <span class="font-medium">{{ $t('cart.title.animals') }} ({{ animals.length }})</span>

          <AShoppingCartIconButton
            v-if="canEditSummaryItems"
            :icon="isEditingAnimals ? 'pi pi-save' : 'pi pi-pencil'"
            @onClick="isEditingAnimals = !isEditingAnimals"
          />
        </div>
      </template>

      <div class="flex flex-col flex-wrap gap-8">
        <MShoppingCartSummaryAnimal
          v-for="(animal, index) in animals"
          :key="animal._id"
          :animal
          :isEditingAnimals
          :isLastAnimal="index === animals.length - 1"
        >
          <template
            v-if="showMedicalCosts && hasCart"
            #medical
          >
            <MShoppingCartSummaryMedical
              :animal
              :canEditSummaryItems
              :isEditingAnimals
            />
          </template>
        </MShoppingCartSummaryAnimal>
      </div>
    </MPanel>

    <template v-if="showRetailSummary">
      <Divider
        v-if="animals.length > 0"
        class="mt-4 before:border-t-[3px]"
      />

      <MPanel
        v-model:collapsed="productsInCollapsed"
        class="h-full border-none"
        pt:content:class="flex-1 shrink-0 basis-auto px-0"
        pt:footer:class="shrink-0"
        pt:header:class="px-0 pb-0"
        pt:toggleableContent="h-full flex flex-col"
        customHeaderClasses="justify-between w-full"
      >
        <template #customHeader>
          <div class="flex w-full flex-wrap items-center justify-between gap-2 pr-4">
            <div class="flex items-center gap-2">
              <span class="font-medium">{{ $t('cart.title.retail') }} ({{ products.length + services.length }})</span>

              <AShoppingCartIconButton
                v-if="products.length + services.length > 0"
                :icon="isEditingProducts ? 'pi pi-save' : 'pi pi-pencil'"
                @onClick="isEditingProducts = !isEditingProducts"
              />
            </div>

            <AShoppingCartRetailPresetDiscountSelector
              v-if="products.length + services.length > 0"
              v-show="!productsInCollapsed"
              label="cart.summary.promoProducts"
              :disabled="!isEditingProducts"
              :isEditingProducts
            />
          </div>
        </template>

        <MShoppingCartSummaryProducts
          :canEditItems="canEditSummaryItems"
          :isEditingProducts
          :products
          :services
        />
      </MPanel>
    </template>
  </div>
</template>
